import React, { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { List, CaretDown, SignOut } from "@phosphor-icons/react";

export default function GuestNav({ sections, hasCalendar, onExit }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (key) => {
    const el = document.getElementById(key);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // group sections by category
  const groups = sections.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <header
      className={
        "fixed top-0 left-0 right-0 z-40 transition-all duration-500 " +
        (scrolled
          ? "backdrop-blur-xl bg-background/80 border-b border-border py-3"
          : "bg-transparent py-5")
      }
      data-testid="guest-nav"
    >
      <div className="px-6 sm:px-10 lg:px-16 flex items-center justify-end">
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-menu-btn"
                className={
                  "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
                  (scrolled
                    ? "bg-foreground text-primary-foreground hover:bg-accent"
                    : "bg-[#F7F6F2]/15 text-[#F7F6F2] backdrop-blur-md hover:bg-[#F7F6F2]/25")
                }
              >
                <List size={16} weight="bold" />
                Guide
                <CaretDown size={12} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-lenis-prevent className="w-64 max-h-[70vh] overflow-y-auto bg-popover border-border">
              {Object.entries(groups).map(([cat, items]) => (
                <div key={cat}>
                  <DropdownMenuLabel className="overline text-accent text-[0.6rem]">{cat}</DropdownMenuLabel>
                  {items.map((s) => (
                    <DropdownMenuItem
                      key={s.key}
                      data-testid={`nav-jump-${s.key}`}
                      onClick={() => jump(s.key)}
                      className="cursor-pointer gap-3"
                    >
                      <span className="text-xs text-muted-foreground w-5">{s.number}</span>
                      <span className="text-sm">{s.title}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
              <DropdownMenuItem
                data-testid="nav-report"
                onClick={() => {
                  const el = document.getElementById("report-issue");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="cursor-pointer text-accent"
              >
                Report a maintenance issue
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="nav-exit"
                onClick={onExit}
                className="cursor-pointer text-destructive"
              >
                Exit guidebook
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {hasCalendar && (
            <button
              onClick={() => jump("calendar")}
              data-testid="nav-calendar-btn"
              className={
                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
                (scrolled
                  ? "border border-border text-foreground hover:bg-secondary"
                  : "bg-[#F7F6F2]/15 text-[#F7F6F2] backdrop-blur-md hover:bg-[#F7F6F2]/25")
              }
            >
              Calendar
            </button>
          )}

          <button
            onClick={onExit}
            data-testid="guest-exit-btn"
            aria-label="Exit guidebook"
            className={
              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
              (scrolled
                ? "border border-border text-foreground hover:bg-secondary"
                : "bg-[#F7F6F2]/10 text-[#F7F6F2] backdrop-blur-md hover:bg-[#F7F6F2]/25")
            }
          >
            <SignOut size={16} weight="bold" />
            <span className="hidden sm:inline">Exit</span>
          </button>
        </div>
      </div>
    </header>
  );
}
